import * as React from 'react';
import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  ColumnLayout,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react-v3';
import { DATAPROVIDER_AND_CONSUMER } from 'src/commons/constants';
import { getRequestSubmittedWithAutoApproval } from 'src/commons/common';
import * as validate from 'src/commons/validationUtils';
import { editGroup, isMemberOfGroupingMechanismId } from 'src/api/permissions';
import { SelectProps } from '@amzn/awsui-components-react-v3/polaris/select/interfaces';

export interface UpdateGroupModalProps {
  visible: boolean;
  setVisible: any;
  setRedirect: any;
  setNotifications: any;
  groupId: string;
  activeGroupInfo: any;
}

export const UpdateGroupModal = (props: UpdateGroupModalProps) => {
  const [teamWiki, setTeamWiki] = useState<string>('');
  const [teamEmail, setTeamEmail] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [item, setItem] = useState<string>('');
  const [useCase, setUseCase] = useState<string>('');
  const [dataAccessAndProcessing, setDataAccessAndProcessing] = useState<string>('');
  const [optionLabel, setOptionLabel] = useState<string>('');
  const [customerType, setCustomerType] = useState<string>('');
  const [, setCustomerTypeOptionLabel] = useState<string>('');
  const [groupingMechanismId, setGroupingMechanismId] = useState<string>('');

  const [teamWikiValidation, setTeamWikiValidation] = useState<boolean>(false);
  const [categoryValidation, setCategoryValidation] = useState<boolean>(false);
  const [typeValidation, setTypeValidation] = useState<boolean>(false);
  const [itemValidation, setItemValidation] = useState<boolean>(false);
  const [teamWikiErrorText, setTeamWikiErrorText] = useState<string>('');
  const [teamEmailValidation, setTeamEmailValidation] = useState<boolean>(false);
  const [teamEmailErrorText, setTeamEmailErrorText] = useState<string>('');
  const [useCaseValidation, setUseCaseValidation] = useState<boolean>(false);
  const [dataAccessAndProcessingOption, setDataAccessAndProcessingOption] = useState<SelectProps.Option>(undefined);
  const [dataAccessAndProcessingValidation, setDataAccessAndProcessingValidation] = useState<boolean>(false);
  const [dataAccessAndProcessingErrorText, setDataAccessAndProcessingErrorText] = useState<string>('');
  const [customerTypeOption, setCustomerTypeOption] = useState<SelectProps.Option>(undefined);
  const [customerTypeValidation, setCustomerTypeValidation] = useState<boolean>(false);
  const [customerTypeErrorText] = useState<string>('');
  const [teamGroupingMechanismIdValidation, setTeamGroupingMechanismIdValidation] = useState<boolean>(false);
  const [teamGroupingMechanismIdErrorText, setTeamGroupingMechanismIdErrorText] = useState<string>('');

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const dataAccessOptions = [
    {
      label: 'Amazon EMR',
      id: '1',
    },
    {
      label: 'Amazon Redshift',
      id: '2',
    },
    {
      label: 'Amazon Redshift Spectrum',
      id: '3',
    },
    {
      label: 'Other',
      id: '4',
    },
  ];

  const customerTypeOptions = [
    {
      label: "I'm a Data Consumer",
      id: 'DATACONSUMER',
      value: 'DATACONSUMER',
    },
    {
      label: "I'm a Data Provider",
      id: 'DATAPROVIDER',
      value: 'DATAPROVIDER',
    },
    {
      label: 'Both',
      id: 'DATAPROVIDER_AND_DATACONSUMER',
      value: 'DATAPROVIDER_AND_DATACONSUMER',
    },
  ];

  useEffect(() => {
    initializeEditableParameters();
  }, [props.activeGroupInfo]);

  const findDataAccessId = (dataAccessAndProcessing) => {
    const option = dataAccessOptions.filter((item) => item.label === dataAccessAndProcessing);

    return option.length !== 0 ? option[0].id : dataAccessOptions.filter((item) => item.label === 'Other')[0].id;
  };

  // this will initialize editable fields with the current fields, so they can be prefilled in form
  const initializeEditableParameters = () => {
    if (!props.activeGroupInfo) return;
    const { teamInfo } = props.activeGroupInfo;

    const currentDataAccessAndProcessing = teamInfo.DataAccessAndProcessing;
    // Default as DATAPROVIDER_AND_CONSUMER if CustomerType field is not associated with the group
    const currentCustomerType = teamInfo.CustomerType ? teamInfo.CustomerType : DATAPROVIDER_AND_CONSUMER;
    // setting up the retrieved value for DataClassification to be displayed

    const customerTypeIndex = customerTypeOptions.find((item) => item.id === currentCustomerType);
    const dataAccessId = findDataAccessId(currentDataAccessAndProcessing);
    const dataAccessAndProcessing = dataAccessOptions.find((item) => item.id === dataAccessId);

    setUseCase(teamInfo.UseCase);
    setTeamEmail(teamInfo.DistributionList);
    setTeamWiki(teamInfo.Wiki);
    setGroupingMechanismId(props.activeGroupInfo?.groupingMechanismId);
    setOptionLabel(dataAccessAndProcessing.label);
    setDataAccessAndProcessingOption(dataAccessAndProcessing);
    setCategory(props.activeGroupInfo.primaryCTI == undefined ? '' : props.activeGroupInfo.primaryCTI.category);
    setType(props.activeGroupInfo.primaryCTI == undefined ? '' : props.activeGroupInfo.primaryCTI.type);
    setItem(props.activeGroupInfo.primaryCTI == undefined ? '' : props.activeGroupInfo.primaryCTI.item);

    setDataAccessAndProcessing(teamInfo.DataAccessAndProcessing);
    setCustomerTypeOption(customerTypeIndex);
    setCustomerType(customerTypeIndex.id);
  };

  const handleUpdateGroup = async () => {
    setButtonLoading(true);
    const validated = await validateEditGroupRequest();
    if (!validated) {
      setButtonLoading(false);
      return;
    }
    try {
      await submitEditGroupRequest();
      props.setVisible(false);
      props.setNotifications('', getRequestSubmittedWithAutoApproval());
    } catch (e) {
      props.setVisible(false);
      let errorMessage = 'Please contact Data Lake oncall to help you with your request';
      props.setNotifications('Failed to submit your request', errorMessage);
    }
    setButtonLoading(false);
  };

  const validateEditGroupRequest = async () => {
    if (
      validate.isFalsy(teamWiki) ||
      validate.isFalsy(teamEmail) ||
      validate.isFalsy(useCase) ||
      validate.isFalsy(dataAccessAndProcessing) ||
      validate.isFalsy(customerType) ||
      validate.isFalsy(groupingMechanismId) ||
      validate.isFalsy(category) ||
      validate.isFalsy(type) ||
      validate.isFalsy(item)
    ) {
      props.setNotifications('Empty request fields found', '');
      return false;
    }
    if (validate.isNotFalsy(teamWiki)) {
      const validWiki = validate.isValidUrl(teamWiki);
      if (!validWiki) {
        setTeamWikiValidation(true);
        setTeamWikiErrorText('Invalid URL found');
        return false;
      }
    }
    // If there was a change of ID
    if (validate.isNotFalsy(groupingMechanismId) && props.activeGroupInfo?.groupingMechanismId != groupingMechanismId) {
      // Calling Gladstone to check if current user is members of
      // the given grouping mechanism ID
      const result = await isMemberOfGroupingMechanismId({
        groupingMechanismId,
      });
      // If the current user is not a member of the provided group
      if (!result.memberOf) {
        setTeamGroupingMechanismIdValidation(true);
        setTeamGroupingMechanismIdErrorText('You are not a member of the provided group ID');
        return false;
      }
    }

    if (validate.isNotFalsy(teamEmail)) {
      const validTeamEmail = validate.isValidAmazonEmail(teamEmail);
      if (!validTeamEmail) {
        setTeamEmailValidation(true);
        setTeamEmailErrorText('Invalid team email found');
        return false;
      }
    }

    if (optionLabel === 'Other' && (dataAccessAndProcessing.length === 0 || !dataAccessAndProcessing.trim())) {
      setDataAccessAndProcessingValidation(true);
      setDataAccessAndProcessingErrorText('Data access and processing description must not be empty');
      return false;
    }
    return true;
  };

  const submitEditGroupRequest = async () => {
    if (validate.isFalsy(teamWiki)) {
      setTeamWiki(undefined);
    }
    if (validate.isFalsy(groupingMechanismId)) {
      setGroupingMechanismId(undefined);
    }
    if (validate.isFalsy(teamEmail)) {
      setTeamEmail(undefined);
    }
    if (validate.isFalsy(useCase)) {
      setUseCase(undefined);
    }
    if (validate.isFalsy(dataAccessAndProcessing)) {
      setDataAccessAndProcessing(undefined);
    }
    if (validate.isFalsy(customerType)) {
      setCustomerType(undefined);
    }
    await editGroup({
      groupId: props.groupId,
      teamWiki,
      groupingMechanismId,
      teamDistributionList: teamEmail,
      useCase,
      dataAccessAndProcessing,
      customerType,
      primaryCTI: {
        category: category,
        type: type,
        item: item,
      },
    });
  };

  return (
    <Modal
      onDismiss={() => {
        props.setVisible(false);
      }}
      visible={props.visible}
      size='medium'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button
              variant='link'
              onClick={() => {
                props.setVisible(false);
              }}
            >
              Cancel
            </Button>
            <Button variant='primary' onClick={handleUpdateGroup} loading={buttonLoading}>
              Update group
            </Button>
          </SpaceBetween>
        </Box>
      }
      header='Edit details'
    >
      <ColumnLayout>
        <FormField
          label='Customer type'
          description='What type of customer do you plan on being?'
          errorText={customerTypeValidation ? customerTypeErrorText : false}
        >
          <Select
            selectedOption={customerTypeOption}
            options={customerTypeOptions}
            selectedAriaLabel='Selected'
            onChange={(e) => {
              setCustomerTypeOptionLabel(e.detail.selectedOption.label);
              setCustomerTypeOption(e.detail.selectedOption);
              setCustomerType(e.detail.selectedOption.value);
              setCustomerTypeValidation(false);
            }}
          />
        </FormField>
        <FormField
          label={'Grouping ID'}
          description='Please enter either a POSIX group or a Teams ID. This ID will be used to determine your group members.'
          errorText={teamGroupingMechanismIdValidation ? teamGroupingMechanismIdErrorText : false}
        >
          <Input
            ariaRequired={false}
            value={groupingMechanismId}
            onChange={(e) => {
              setGroupingMechanismId(e.detail.value);
              setTeamGroupingMechanismIdValidation(false);
            }}
          />
        </FormField>
        <FormField label='Team wiki' errorText={teamWikiValidation ? teamWikiErrorText : false}>
          <Input ariaRequired={true} value={teamWiki} onChange={(e) => setTeamWiki(e.detail.value)} />
        </FormField>

        <FormField label='Team email' errorText={teamEmailValidation ? teamEmailErrorText : false}>
          <Input
            ariaRequired={false}
            value={teamEmail}
            onChange={(e) => {
              setTeamEmail(e.detail.value);
              setTeamEmailValidation(false);
            }}
          />
        </FormField>

        <FormField label='Category (C)' errorText={categoryValidation ? 'Category not found' : false}>
          <Input
            ariaRequired={false}
            value={category}
            onChange={(e) => {
              setCategory(e.detail.value);
              setCategoryValidation(false);
            }}
          />
        </FormField>

        <FormField label='Type (T)' errorText={typeValidation ? 'Type not found' : false}>
          <Input
            ariaRequired={false}
            value={type}
            onChange={(e) => {
              setType(e.detail.value);
              setTypeValidation(false);
            }}
          />
        </FormField>

        <FormField label='Item (I)' errorText={itemValidation ? 'Item not found' : false}>
          <Input
            ariaRequired={false}
            value={item}
            onChange={(e) => {
              setItem(e.detail.value);
              setItemValidation(false);
            }}
          />
        </FormField>

        <FormField
          label='Data access and processing'
          description='How are you looking to access and process the data?'
          errorText={dataAccessAndProcessingValidation ? dataAccessAndProcessingErrorText : false}
        >
          <Select
            options={dataAccessOptions}
            selectedOption={dataAccessAndProcessingOption}
            selectedAriaLabel='Selected'
            onChange={(e) => {
              setOptionLabel(e.detail.selectedOption.label);
              setDataAccessAndProcessingOption(e.detail.selectedOption);
              setDataAccessAndProcessing('');
              setDataAccessAndProcessingValidation(false);
              if (optionLabel != 'Other') {
                setDataAccessAndProcessing(e.detail.selectedOption.label);
              }
            }}
          />
        </FormField>
        {optionLabel === 'Other' && (
          <>
            <FormField
              description='Explain your way of data access and processing.'
              errorText={dataAccessAndProcessingValidation ? dataAccessAndProcessingErrorText : false}
            >
              <Textarea
                ariaRequired={true}
                value={dataAccessAndProcessing}
                onChange={(e) => {
                  setDataAccessAndProcessing(e.detail.value);
                  setDataAccessAndProcessingValidation(false);
                }}
              />
            </FormField>
          </>
        )}

        <FormField
          label='Use case'
          description='Explain your use case and the business problem that it is solving.'
          errorText={useCaseValidation ? 'Use case not found' : false}
        >
          <Textarea
            ariaRequired={false}
            placeholder={'Use case'}
            value={useCase}
            onChange={(e) => {
              setUseCase(e.detail.value);
              setUseCaseValidation(false);
            }}
          />
        </FormField>
      </ColumnLayout>
    </Modal>
  );
};
